import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Mail, MessageCircle, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';

interface PreviewTemplate {
  code: string;
  channel: 'email' | 'whatsapp';
  trigger: string;
  subject: string;
  html: string;
  text: string;
  default_recipient: string;
}

interface PreviewData {
  tenant: { id: number; name: string; status: string };
  payload_used: Record<string, unknown>;
  templates: PreviewTemplate[];
}

interface Props {
  tenantId: number;
  onClose: () => void;
}

export function NotificationPreviewModal({ tenantId, onClose }: Props) {
  const [selected, setSelected] = useState<string>('');
  const [testRecipient, setTestRecipient] = useState('');

  const { data, isLoading } = useQuery<PreviewData>({
    queryKey: ['notification-preview', tenantId],
    queryFn: () => apiClient.admin.notificationPreview(tenantId).then(r => r.data),
  });

  const testMut = useMutation({
    mutationFn: (vars: { template: PreviewTemplate; recipient: string }) =>
      apiClient.admin.notificationTest(tenantId, {
        template_code: vars.template.code,
        channel: vars.template.channel,
        recipient: vars.recipient,
      }),
    onSuccess: () => toast.success('Enfileirado — vai sair no próximo ciclo do worker (60s)'),
    onError: () => toast.error('Falha ao enfileirar'),
  });

  const list = data?.templates ?? [];

  // Seleciona o primeiro automaticamente quando carrega.
  useEffect(() => {
    if (!selected && list.length > 0) setSelected(list[0].code + ':' + list[0].channel);
  }, [list, selected]);

  const current = useMemo(
    () => list.find(t => t.code + ':' + t.channel === selected),
    [list, selected]
  );

  useEffect(() => {
    if (current && !testRecipient) setTestRecipient(current.default_recipient);
  }, [current]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-bold">Preview de notificações</h2>
            <p className="text-sm text-muted-foreground">
              {data?.tenant.name ?? '...'} · status: <span className="font-mono">{data?.tenant.status ?? '-'}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-md">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar — lista dos 7 templates */}
          <div className="w-72 border-r border-border overflow-y-auto bg-muted/20">
            {isLoading && <div className="p-4 text-sm text-muted-foreground">Carregando...</div>}
            {list.map((t) => {
              const key = t.code + ':' + t.channel;
              const active = selected === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelected(key)}
                  className={`w-full text-left px-4 py-3 border-b border-border/50 ${active ? 'bg-white' : 'hover:bg-white/60'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {t.channel === 'email' ? (
                      <Mail className="h-3.5 w-3.5 text-sky-600" />
                    ) : (
                      <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                    )}
                    <span className="text-xs font-mono text-muted-foreground">{t.code}</span>
                    <Badge variant={t.channel === 'email' ? 'default' : 'success'} className="ml-auto text-[10px]">
                      {t.channel}
                    </Badge>
                  </div>
                  <div className="text-sm font-medium truncate">{t.subject || t.text.split('\n')[0]}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{t.trigger}</div>
                </button>
              );
            })}
          </div>

          {/* Preview pane */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {current && (
              <>
                <div className="px-6 py-3 border-b border-border bg-muted/10 text-xs space-y-1">
                  <div><span className="text-muted-foreground">Gatilho:</span> {current.trigger}</div>
                  {current.channel === 'email' && (
                    <div><span className="text-muted-foreground">Assunto:</span> <strong>{current.subject}</strong></div>
                  )}
                  <div><span className="text-muted-foreground">Destinatário padrão:</span> <span className="font-mono">{current.default_recipient}</span></div>
                </div>

                <div className="flex-1 overflow-auto bg-slate-100 p-4">
                  {current.channel === 'email' ? (
                    <iframe
                      title={current.code}
                      srcDoc={current.html}
                      className="w-full h-full bg-white rounded-lg border border-border shadow-sm"
                    />
                  ) : (
                    <div className="max-w-md mx-auto">
                      <div className="bg-[#dcf8c6] rounded-2xl px-4 py-3 shadow-sm whitespace-pre-wrap text-sm leading-relaxed">
                        {current.text}
                      </div>
                      <div className="text-xs text-muted-foreground text-center mt-2">Preview WhatsApp</div>
                    </div>
                  )}
                </div>

                {/* Texto plano (sempre visível pra debug) */}
                {current.channel === 'email' && (
                  <details className="border-t border-border">
                    <summary className="px-6 py-2 text-xs text-muted-foreground cursor-pointer">
                      Ver versão texto puro (fallback)
                    </summary>
                    <pre className="px-6 py-3 text-xs whitespace-pre-wrap bg-slate-50 max-h-40 overflow-auto">{current.text}</pre>
                  </details>
                )}

                {/* Send test */}
                <div className="px-6 py-3 border-t border-border bg-white flex items-center gap-2">
                  <Input
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    placeholder={current.channel === 'email' ? 'seu@email.com' : '+55 11 9...'}
                    className="flex-1"
                  />
                  <Button
                    size="sm"
                    disabled={!testRecipient || testMut.isPending}
                    onClick={() => testMut.mutate({ template: current, recipient: testRecipient })}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {testMut.isPending ? 'Enviando...' : 'Enviar teste'}
                  </Button>
                </div>
                <div className="px-6 pb-3 text-[11px] text-muted-foreground">
                  Enfileira no outbox real. Vai sair quando AWS SES / ZapShow estiverem configurados.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
